import type { RegistrantType } from '@centrix/api-client';

// Local-part and domain must contain only allowed characters, may not start
// or end with a dot, may not contain consecutive dots, and the domain TLD must
// be at least two letters. Rejects nonsense formats like ".@..." which the
// previous loose pattern accepted.
const EMAIL_PATTERN =
  /^[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[A-Za-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?\.)+[A-Za-z]{2,}$/;
const MIN_PHONE_DIGITS = 10;
const MAX_PHONE_DIGITS = 15;
const MIN_ORG_NAME_LENGTH = 3;
const MIN_ORG_NAME_LETTERS = 2;
// Must begin with a letter or digit — rejects leading dots, dashes, etc.
const ORG_NAME_START_PATTERN = /^[A-Za-z0-9]/;
// Two or more consecutive non-alphanumeric, non-space characters (e.g. "..", "--", "..a" after trimming).
const ORG_NAME_CONSECUTIVE_SPECIALS_PATTERN = /[^A-Za-z0-9\s]{2,}/;
const PASSWORD_MIN_LENGTH = 8;
const PASSWORD_PATTERNS = {
  lower: /[a-z]/,
  upper: /[A-Z]/,
  digit: /\d/,
  symbol: /[^A-Za-z0-9]/,
} as const;

// Convert a user-typed international phone number into the `+`-prefixed E.164
// shape the backend accepts. Users frequently enter ITU "00" trunk-style
// numbers (e.g. `00905314905710` for Turkey); those must be rewritten to
// `+905314905710` before submission or the backend rejects them as invalid.
export function normalisePhoneForSubmit(raw: string): string {
  const trimmed = raw.trim();
  if (!trimmed) return trimmed;
  const compact = trimmed.replace(/[\s\-().]/g, '');
  if (compact.startsWith('00')) return `+${compact.slice(2)}`;
  return compact;
}

export interface CredentialsValidationInput {
  email: string;
  password: string;
}

export interface CredentialsValidationErrors {
  email?: string;
  password?: string;
}

export interface SignupValidationInput extends CredentialsValidationInput {
  orgName: string;
  phone: string;
  registrationType: RegistrantType;
}

export interface SignupValidationErrors extends CredentialsValidationErrors {
  orgName?: string;
  phone?: string;
}

export function validateCredentials({
  email,
  password,
}: CredentialsValidationInput): CredentialsValidationErrors {
  const errors: CredentialsValidationErrors = {};

  if (!EMAIL_PATTERN.test(email.trim())) {
    errors.email = 'Please enter a valid email address.';
  }

  if (password.length < PASSWORD_MIN_LENGTH) {
    errors.password = `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`;
  }

  return errors;
}

export function validatePasswordComplexity(password: string): string | undefined {
  if (password.length < PASSWORD_MIN_LENGTH) {
    return `Password must be at least ${PASSWORD_MIN_LENGTH} characters.`;
  }

  const missing: string[] = [];
  if (!PASSWORD_PATTERNS.lower.test(password)) missing.push('a lowercase letter');
  if (!PASSWORD_PATTERNS.upper.test(password)) missing.push('an uppercase letter');
  if (!PASSWORD_PATTERNS.digit.test(password)) missing.push('a number');
  if (!PASSWORD_PATTERNS.symbol.test(password)) missing.push('a symbol');

  if (missing.length > 0) {
    return `Password must include ${missing.join(', ')}.`;
  }

  return undefined;
}

export function validateEmail(email: string): string | undefined {
  if (!EMAIL_PATTERN.test(email.trim())) {
    return 'Please enter a valid email address.';
  }
  return undefined;
}

export function validateOrgName(orgName: string): string | undefined {
  const trimmed = orgName.trim();
  if (!trimmed) return 'Organisation name is required.';
  if (trimmed.length < MIN_ORG_NAME_LENGTH) {
    return `Organisation name must be at least ${MIN_ORG_NAME_LENGTH} characters.`;
  }
  if (!ORG_NAME_START_PATTERN.test(trimmed)) {
    return 'Organisation name must start with a letter or number.';
  }
  const letterCount = (trimmed.match(/[A-Za-z]/g) ?? []).length;
  if (letterCount < MIN_ORG_NAME_LETTERS) {
    return 'Organisation name must contain at least 2 letters.';
  }
  if (ORG_NAME_CONSECUTIVE_SPECIALS_PATTERN.test(trimmed)) {
    return 'Organisation name must not contain consecutive special characters (e.g. ".." or "--").';
  }
  return undefined;
}

export interface ResetPasswordValidationInput {
  newPassword: string;
  confirmPassword: string;
}

export interface ResetPasswordValidationErrors {
  newPassword?: string;
  confirmPassword?: string;
}

export function validateResetPassword({
  newPassword,
  confirmPassword,
}: ResetPasswordValidationInput): ResetPasswordValidationErrors {
  const errors: ResetPasswordValidationErrors = {};

  const complexityError = validatePasswordComplexity(newPassword);
  if (complexityError) {
    errors.newPassword = complexityError;
  }

  if (!confirmPassword) {
    errors.confirmPassword = 'Please re-enter your password.';
  } else if (newPassword !== confirmPassword) {
    errors.confirmPassword = 'Passwords do not match.';
  }

  return errors;
}

export function validateSignupFields({
  email,
  password,
  orgName,
  phone,
  registrationType,
}: SignupValidationInput): SignupValidationErrors {
  const errors: SignupValidationErrors = {
    ...validateCredentials({ email, password }),
  };

  const passwordComplexityError = validatePasswordComplexity(password);
  if (passwordComplexityError) {
    errors.password = passwordComplexityError;
  }

  if (!phone.trim()) {
    errors.phone = 'Phone number is required.';
  } else {
    const digits = phone.replace(/\D/g, '');
    const startsWithDoubleZero = digits.startsWith('00');
    const significantDigits = startsWithDoubleZero ? digits.slice(2) : digits;
    if (
      significantDigits.length < MIN_PHONE_DIGITS ||
      significantDigits.length > MAX_PHONE_DIGITS
    ) {
      errors.phone = `Please enter a valid phone number with at least ${MIN_PHONE_DIGITS} digits.`;
    }
  }

  if (registrationType === 'company') {
    const orgNameError = validateOrgName(orgName);
    if (orgNameError) errors.orgName = orgNameError;
  }

  return errors;
}
